import { useState, useCallback, useEffect, useRef } from "react";
import { useApiRequest } from "../../../hooks/useApiRequest";
import type {
  MissionDefinition,
  CreateMissionPayload,
  UpdateMissionPayload,
  CreateFieldPayload,
  UpdateFieldPayload,
} from "./types";
import type { FieldChangeset } from "./components/useFormBuilder";

const BASE = import.meta.env.VITE_STARTER_PACK_BASE_URL ?? "";

export function useMissionsAdmin() {
  const { request } = useApiRequest();
  const requestRef = useRef(request);
  requestRef.current = request;

  const [missions, setMissions] = useState<MissionDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const fetchMissions = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const data = await requestRef.current({
        url: `${BASE}/api/v1/admin/missions`,
        method: "GET",
        skipToast: true,
      });
      setMissions(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setFetchError(err?.message ?? "Erro ao carregar missões.");
    } finally {
      setLoading(false);
    }
  }, []); // sem deps: lê sempre o request mais atual via ref

  const createMission = useCallback(async (payload: CreateMissionPayload): Promise<MissionDefinition> => {
    const data = await requestRef.current({
      url: `${BASE}/api/v1/admin/missions`,
      method: "POST",
      payload,
      sucessMesage: "Missão criada com sucesso",
    });
    await fetchMissions();
    return data as MissionDefinition;
  }, [fetchMissions]);

  const updateMission = useCallback(async (id: number, payload: UpdateMissionPayload) => {
    await requestRef.current({
      url: `${BASE}/api/v1/admin/missions/${id}`,
      method: "PATCH",
      payload,
      sucessMesage: "Missão atualizada",
    });
    await fetchMissions();
  }, [fetchMissions]);

  const deleteMission = useCallback(async (id: number) => {
    await requestRef.current({
      url: `${BASE}/api/v1/admin/missions/${id}`,
      method: "PATCH",
      payload: { is_active: false },
      sucessMesage: "Missão removida",
    });
    await fetchMissions();
  }, [fetchMissions]);

  const addField = useCallback(async (missionId: number, payload: CreateFieldPayload) => {
    await requestRef.current({
      url: `${BASE}/api/v1/admin/missions/${missionId}/fields`,
      method: "POST",
      payload,
      sucessMesage: "Campo adicionado",
    });
    await fetchMissions();
  }, [fetchMissions]);

  const updateField = useCallback(async (missionId: number, fieldId: number, payload: UpdateFieldPayload) => {
    await requestRef.current({
      url: `${BASE}/api/v1/admin/missions/${missionId}/fields/${fieldId}`,
      method: "PATCH",
      payload,
      sucessMesage: "Campo atualizado",
    });
    await fetchMissions();
  }, [fetchMissions]);

  const deleteField = useCallback(async (missionId: number, fieldId: number) => {
    await requestRef.current({
      url: `${BASE}/api/v1/admin/missions/${missionId}/fields/${fieldId}`,
      method: "DELETE",
      skipToast: false,
      sucessMesage: "Campo removido",
    });
    await fetchMissions();
  }, [fetchMissions]);

  const bulkUpdateFields = useCallback(async (missionId: number, changeset: FieldChangeset) => {
    if (!changeset.toAdd.length && !changeset.toUpdate.length && !changeset.toDelete.length) return;
    const updatedMission = await requestRef.current({
      url: `${BASE}/api/v1/admin/missions/${missionId}/fields/bulk`,
      method: "PUT",
      payload: {
        to_add: changeset.toAdd,
        to_update: changeset.toUpdate,
        to_delete: changeset.toDelete,
      },
      sucessMesage: "Campos salvos",
    });
    await fetchMissions();
    return updatedMission;
  }, [fetchMissions]);

  useEffect(() => {
    const controller = new AbortController();

    const timerId = setTimeout(() => {
      setLoading(true);
      setFetchError(null);
      requestRef.current({
        url: `${BASE}/api/v1/admin/missions`,
        method: "GET",
        skipToast: true,
        signal: controller.signal,
      })
        .then((data) => setMissions(Array.isArray(data) ? data : []))
        .catch((err) => {
          if (err?.name === "CanceledError" || err?.name === "AbortError") return;
          setFetchError(err?.message ?? "Erro ao carregar missões.");
        })
        .finally(() => setLoading(false));
    }, 0);

    return () => {
      clearTimeout(timerId);
      controller.abort();
    };
  }, []);

  return {
    missions,
    loading,
    fetchError,
    fetchMissions,
    createMission,
    updateMission,
    deleteMission,
    addField,
    updateField,
    deleteField,
    bulkUpdateFields,
  };
}
